import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import alert from "components/elements/Alert";

import { generateRandomString } from "utils/helper";
import * as services from "../services/optionsApi";
import * as slices from "../slices/optionsSlice";

function modifyOptions(options = [], lang = "en") {
  return options.map((opt) => ({
    ...opt,
    label: lang === "en" ? opt.nameEn : opt.name,
    value: opt.id,
  }));
}
function simplifyArrayToNested(array) {
  const map = new Map();
  const result = [];

  for (const obj of array) {
    obj.children = [];
    map?.set(obj?.organizationId, obj);
  }

  for (const obj of array) {
    if ((obj?.under?.id || obj?.under?.id === "") && map?.has(obj?.under?.id)) {
      const parent = map?.get(obj?.under?.id);
      parent?.children?.push(obj);
    } else {
      result.push(obj);
    }
  }
  return result;
}
function useOptions() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const [lang, setLang] = useState("en");
  const isLoading = useSelector((state) => state.options.isLoading);
  const mobileCodes = useSelector((state) => state.options.mobileCodes);
  const companyTypeOptions = useSelector(
    (state) => state.options.companyTypeOptions
  );
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const businessTypeOptions = useSelector(
    (state) => state.options.businessTypeOptions
  );
  const provinceOptions = useSelector((state) => state.options.provinceOptions);
  const districtOptions = useSelector((state) => state.options.districtOptions);
  const subDistrictOptions = useSelector(
    (state) => state.options.subDistrictOptions
  );
  const registered_provinceOptions = useSelector(
    (state) => state.options.registered_provinceOptions
  );
  const registered_districtOptions = useSelector(
    (state) => state.options.registered_districtOptions
  );
  const registered_subDistrictOptions = useSelector(
    (state) => state.options.registered_subDistrictOptions
  );
  const shiftConditionChangeOptions = useSelector(
    (state) => state.options.shiftConditionChangeOptions
  );
  const otReqConditionOptions = useSelector(
    (state) => state.options.otReqConditionOptions
  );
  const breakPeriodTypeOptions = useSelector(
    (state) => state.options.breakPeriodTypeOptions
  );
  const holidayTypeOptions = useSelector(
    (state) => state.options.holidayTypeOptions
  );
  const deviceTypeOptions = useSelector(
    (state) => state.options.deviceTypeOptions
  );
  const incomeDeductionByTypeOptions = useSelector(
    (state) => state.options.incomeDeductionByTypeOptions
  );
  const deductionByTypeOptions = useSelector(
    (state) => state.options.deductionByTypeOptions
  );
  const incomeByCompanyOptions = useSelector(
    (state) => state.options.incomeByCompanyOptions
  );
  const noAmountOptions = useSelector(
    (state) => state.options.noAmountOptions
  );
  const noWorkHoursOptions = useSelector(
    (state) => state.options.noWorkHoursOptions
  );
  const noWorkDaysOptions = useSelector(
    (state) => state.options.noWorkDaysOptions
  );
  const deductByCompanyOptions = useSelector(
    (state) => state.options.deductByCompanyOptions
  );
  const roundingTypeOptions = useSelector(
    (state) => state.options.roundingTypeOptions
  );
  const employmentSetupOptions = useSelector(
    (state) => state.options.employmentSetupOptions
  );
  const frequencyOfPaymentOptions = useSelector(
    (state) => state.options.frequencyOfPaymentOptions
  );
  const taxCalculationOptions = useSelector(
    (state) => state.options.taxCalculationOptions
  );
  const reasonForSSOOptions = useSelector(
    (state) => state.options.reasonForSSOOptions
  );
  const employeeTypeOptions = useSelector(
    (state) => state.options.employeeTypeOptions
  );
  const employeeStatusTypeOptions = useSelector(
    (state) => state.options.employeeStatusTypeOptions
  );
  const titleNameOptions = useSelector(
    (state) => state.options.titleNameOptions
  );
  const titleNameEnOptions = useSelector(
    (state) => state.options.titleNameEnOptions
  );
  const welfareTypeOptions = useSelector(
    (state) => state.options.welfareTypeOptions
  );
  const rolePermissionOptions = useSelector(
    (state) => state.options.rolePermissionOptions
  );
  const periodTimeUnitOptions = useSelector(
    (state) => state.options.periodTimeUnitOptions
  );
  const genderOptions = useSelector((state) => state.options.genderOptions);
  const welfareLimitOptions = useSelector(
    (state) => state.options.welfareLimitOptions
  );
  const attendanceConditionOptions = useSelector(
    (state) => state.options.attendanceConditionOptions
  );
  const checkInChanelOptions = useSelector(
    (state) => state.options.checkInChanelOptions
  );
  const weekDayWorkingOptions = useSelector(
    (state) => state.options.weekDayWorkingOptions
  );
  const weekWorkingOptions = useSelector(
    (state) => state.options.weekWorkingOptions
  );
  const socialSecurityBranchOptions = useSelector(
    (state) => state.options.socialSecurityBranchOption
  );
  const acountTypeOption = useSelector(
    (state) => state.options.accountTypeOption
  );
  const welfarePaidOptions = useSelector(
    (state) => state.options.welfarePaidOptions
  );
  const typeOfSlipOptions = useSelector(
    (state) => state.options.typeOfSlipOptions
  );
  const leaveGroupOptions = useSelector(
    (state) => state.options.leaveGroupOptions
  );
  const leaveRequestConditionOptions = useSelector(
    (state) => state.options.leaveRequestConditionOptions
  );
  const minimumLeaveGroupOptions = useSelector(
    (state) => state.options.minimumLeaveGroupOptions
  );
  const documentLimitGroupOptions = useSelector(
    (state) => state.options.documentLimitGroupOptions
  );
  const monthWeekDayWorkingOptions = useSelector(
    (state) => state.options.monthWeekDayWorkingOptions
  );
  const leaveCalculationOptions = useSelector(
    (state) => state.options.leaveCalculationOptions
  );
  const raceOptions = useSelector((state) => state.options.raceOptions);
  const nationalityOptions = useSelector(
    (state) => state.options.nationalityOptions
  );
  const countryOptions = useSelector((state) => state.options.countryOptions);
  const visaTypeOptions = useSelector((state) => state.options.visaTypeOptions);
  const religionOptions = useSelector((state) => state.options.religionOptions);
  const maritalStatusOptions = useSelector(
    (state) => state.options.maritalStatusOptions
  );
  const militaryStatusOptions = useSelector(
    (state) => state.options.militaryStatusOptions
  );
  const bloodGroupOptions = useSelector(
    (state) => state.options.bloodGroupOptions
  );
  const relationshipOptions = useSelector(
    (state) => state.options.relationshipOptions);

  const amountIncomeDeductionOptions = useSelector(
    (state) => state.options.amountIncomeDeductionOptions
  );
  const occupationOptions = useSelector(
    (state) => state.options.occupationOptions
  );

  const educationDegreeOptions = useSelector(
    (state) => state.options.educationDegreeOptions
  );
  const informationTypeOptions = useSelector(
    (state) => state.options.informationTypeOptions
  );

  const employmentTypeOptions = useSelector(
    (state) => state.options.employmentTypeOptions
  );

  const bankOptions = useSelector(
    (state) => state.options.bankOptions
  );

  // const currencyOptions = useSelector(
  //   (state) => state.options.currencyOptions
  // );

  const movementGroupOptions = useSelector(
    (state) => state.options.movementGroupOptions
  );

  const gender = useSelector(
    (state) => state.options.gender
  );

  const reasonForSeparationOptions = useSelector(
    (state) => state.options.reasonForSeparationOptions
  );

  // ---------------------------------------------------------------------
  // Company-aware cache guard.
  //
  // Every getter below used to guard itself with `if (data?.length > 0) return;`.
  // That means: once ANY company populated the list, the guard blocked every
  // future fetch forever - including after `currentCompany` changed. This ref
  // remembers which company each resource's cached data belongs to, so a
  // getter only skips its fetch when it already has data AND that data was
  // fetched for the company currently in scope.
  // ---------------------------------------------------------------------
  const companyCacheRef = useRef({});

  const shouldSkipFetch = useCallback(
    (cacheKey, hasData, companyKey = currentCompany) => {
      if (hasData && companyCacheRef.current[cacheKey] === companyKey) {
        return true;
      }
      companyCacheRef.current[cacheKey] = companyKey;
      return false;
    },
    [currentCompany]
  );

  const modifiedMobileCodes = useMemo(() => {
    const modifiedOptions = mobileCodes.map((otp) => ({
      ...otp,
      label: otp.code,
      value: otp.id,
    }));
    return modifiedOptions;
  }, [mobileCodes]);

  const modifiedMstCompanyTypeOptions = useMemo(() => {
    const modifiedOptions = companyTypeOptions.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
      value: otp.id,
    }));
    return modifiedOptions;
  }, [lang, companyTypeOptions]);

  const modifiedBusinessTypeOptions = useMemo(() => {
    const modifiedOptions = businessTypeOptions.map((opt) => ({
      ...opt,
      label: opt.commonTypeName,
      value: generateRandomString(),
    }));
    return modifiedOptions;
  }, [businessTypeOptions]);

  const modifiedProvinceOptions = useMemo(() => {
    const modifiedOptions = provinceOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.provinceNameEn : opt.provinceName,
      value: opt.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [lang, provinceOptions]);

  const modifiedDistrictOptions = useMemo(() => {
    const modifiedOptions = districtOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.districtNameEn : opt.districtName,
      value: opt.districtId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [lang, districtOptions]);

  const modifiedSubDistrictOptions = useMemo(() => {
    const modifiedOptions = subDistrictOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.subDistrictNameEn : opt.subDistrictName,
      value: opt.subDistrictId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [lang, subDistrictOptions]);

  const modifiedShiftConditionChangeOptions = useMemo(() => {
    const modifiedOptions = shiftConditionChangeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, shiftConditionChangeOptions]);

  const modifiedOtReqConditionOptions = useMemo(() => {
    const modifiedOptions = otReqConditionOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, otReqConditionOptions]);

  const modifiedBreakPeriodTypeOptions = useMemo(() => {
    const modifiedOptions = breakPeriodTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, breakPeriodTypeOptions]);

  const modifiedHolidayTypeOptions = useMemo(() => {
    const modifiedOptions = holidayTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, holidayTypeOptions]);

  const modifiedDeviceTypeOptions = useMemo(() => {
    const modifiedOptions = deviceTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, deviceTypeOptions]);

  const modifiedIncomeDeductionByTypeOptions = useMemo(() => {
    const modifiedOptions = incomeDeductionByTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, incomeDeductionByTypeOptions]);
  const modifiedDeductionByTypeOptions = useMemo(() => {
    const modifiedOptions = deductionByTypeOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, deductionByTypeOptions]);

  const modifiedIncomeByCompanyOptions = useMemo(() => {
    const modifiedOptions = incomeByCompanyOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, incomeByCompanyOptions]);
  const modifiedNoAmountOptions = useMemo(() => {
    const modifiedOptions = noAmountOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, noAmountOptions]);
  const modifiedNoWorkHoursOptions = useMemo(() => {
    const modifiedOptions = noWorkHoursOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, noWorkHoursOptions]);
  const modifiedNoWorkDaysOptions = useMemo(() => {
    const modifiedOptions = noWorkDaysOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, noWorkDaysOptions]);
  const modifiedDeductByCompanyOptions = useMemo(() => {
    const modifiedOptions = deductByCompanyOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, deductByCompanyOptions]);

  const modifiedRoundingTypeOptions = useMemo(() => {
    const modifiedOptions = roundingTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, roundingTypeOptions]);
  const modifiedEmploymentSetupOptions = useMemo(() => {
    const modifiedOptions = employmentSetupOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.employmentTypeNameEn : opt.employmentTypeName,
      value: opt.id,
      key: opt.id
    }));
    return modifiedOptions;
  }, [lang, employmentSetupOptions]);

  const modifiedFrequencyOfPaymentOptions = useMemo(() => {
    const modifiedOptions = frequencyOfPaymentOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, frequencyOfPaymentOptions]);

  const modifiedTaxCalculationOptions = useMemo(() => {
    const modifiedOptions = taxCalculationOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, taxCalculationOptions]);

  const modifiedReasonForSSOOptions = useMemo(() => {
    const modifiedOptions = reasonForSSOOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, reasonForSSOOptions]);

  const modifiedReasonForSeparationOptions = useMemo(() => {
    const modifiedOptions = reasonForSeparationOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, reasonForSeparationOptions]);

  const modifiedEmployeeTypeOptions = useMemo(() => {
    const modifiedOptions = employeeTypeOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, employeeTypeOptions]);

  const modifiedEmployeeStatusTypeOptions = useMemo(() => {
    const modifiedOptions = employeeStatusTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions?.sort((a, b) =>
      a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
  }, [lang, employeeStatusTypeOptions]);

  const modifiedTitleNameOptions = useMemo(() => {
    const modifiedOptions = titleNameOptions.map((opt) => ({
      ...opt,
      label: opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [titleNameOptions]);

  const modifiedTitleNameEnOptions = useMemo(() => {
    const modifiedOptions = titleNameEnOptions?.map((opt) => ({
      ...opt,
      label: opt.nameEn,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, titleNameEnOptions]);

  const modifiedWelfareTypeOptions = useMemo(() => {
    const modifiedOptions = welfareTypeOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, welfareTypeOptions]);

  const modifiedRolePermissionOptions = useMemo(() => {
    const modifiedOptions = rolePermissionOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.roleNameEn : opt.roleName,
      value: opt.roleId,
    }));
    return modifiedOptions;
  }, [lang, rolePermissionOptions]);

  const modifiedPeriodTimeUnitOptions = useMemo(() => {
    const modifiedOptions = periodTimeUnitOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, periodTimeUnitOptions]);

  const modifiedSocialSecurityBranchOptions = useMemo(() => {
    const modifiedOptions = socialSecurityBranchOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, socialSecurityBranchOptions]);

  const modifiedGenderOptions = useMemo(() => {
    const modifiedOptions = genderOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, genderOptions]);

  const modifiedGender = useMemo(() => {
    const modifiedOptions = gender.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, gender]);

  const modifiedWelfareLimitOptions = useMemo(() => {
    const modifiedOptions = welfareLimitOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, welfareLimitOptions]);

  const modifiedAttendanceConditionOptions = useMemo(() => {
    const modifiedOptions = attendanceConditionOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, attendanceConditionOptions]);

  const modifiedCheckInChanelOptions = useMemo(() => {
    const modifiedOptions = checkInChanelOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, checkInChanelOptions]);

  const modifiedWeekDayWorkingOptions = useMemo(() => {
    const modifiedOptions = weekDayWorkingOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, weekDayWorkingOptions]);

  const modifiedWeekWorkingOptions = useMemo(() => {
    const modifiedOptions = weekWorkingOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, weekWorkingOptions]);

  const modifiedAcountTypeOption = useMemo(() => {
    const modifiedOptions = acountTypeOption.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, acountTypeOption]);

  const modifiedWelfarePaidOptions = useMemo(() => {
    const modifiedOptions = welfarePaidOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, welfarePaidOptions]);

  const modifiedTypeOfSlipOptions = useMemo(() => {
    const modifiedOptions = typeOfSlipOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, typeOfSlipOptions]);

  const modifiedLeaveGroupOptions = useMemo(() => {
    const modifiedOptions = leaveGroupOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, leaveGroupOptions]);

  const modifiedLeaveRequestConditionOptions = useMemo(() => {
    const modifiedOptions = leaveRequestConditionOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, leaveRequestConditionOptions]);

  const modifiedMinimumLeaveGroupOptions = useMemo(() => {
    const modifiedOptions = minimumLeaveGroupOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: parseInt(opt.code, 10),
    }));
    return modifiedOptions;
  }, [lang, minimumLeaveGroupOptions]);

  const modifiedDocumentLimitGroupOptions = useMemo(() => {
    const modifiedOptions = documentLimitGroupOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: parseInt(opt.code, 10),
    }));
    return modifiedOptions;
  }, [lang, documentLimitGroupOptions]);

  const modifiedMonthWeekDayWorkingOptions = useMemo(() => {
    function sortByMonth(arr) {
      var months = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
      arr.sort(function (a, b) {
        return months.indexOf(a.nameEn)
          - months.indexOf(b.nameEn);
      });
    }
    const modifiedOptions = monthWeekDayWorkingOptions?.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    sortByMonth(modifiedOptions)
    return modifiedOptions;
  }, [lang, monthWeekDayWorkingOptions]);

  const modifiedLeaveCalculationOptions = useMemo(() => {
    const modifiedOptions = leaveCalculationOptions.map((opt) => ({
      ...opt,
      label: lang === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [lang, leaveCalculationOptions]);

  const modifiedRaceOptions = useMemo(() => {
    return modifyOptions(raceOptions, lang);
  }, [lang, raceOptions]);
  const modifiedCountryOptions = useMemo(() => {
    return modifyOptions(countryOptions, lang);
  }, [lang, countryOptions]);

  const modifiedVisaTypeOptions = useMemo(() => {
    return modifyOptions(visaTypeOptions, lang);
  }, [lang, visaTypeOptions]);
  const modifiedNationalityOptions = useMemo(() => {
    return modifyOptions(nationalityOptions, lang);
  }, [lang, nationalityOptions]);

  const modifiedReligionOptions = useMemo(() => {
    return modifyOptions(religionOptions, lang);
  }, [lang, religionOptions]);

  const modifiedMaritalStatusOptions = useMemo(() => {
    return modifyOptions(maritalStatusOptions, lang);
  }, [lang, maritalStatusOptions]);

  const modifiedMilitaryStatusOptions = useMemo(() => {
    return modifyOptions(militaryStatusOptions, lang);
  }, [lang, militaryStatusOptions]);

  const modifiedBloodGroupOptions = useMemo(() => {
    return modifyOptions(bloodGroupOptions, lang);
  }, [lang, bloodGroupOptions]);

  const modifiedRelationshipOptions = useMemo(() => {
    return modifyOptions(relationshipOptions, lang);
  }, [lang, relationshipOptions]);

  const modifiedOccupationOptions = useMemo(() => {
    return modifyOptions(occupationOptions, lang);
  }, [lang, occupationOptions]);

  const modifiedAmountIncomeDeductionOptions = useMemo(() => {
    const modifiedOptions = amountIncomeDeductionOptions?.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
      value: otp.id,
    }));
    return modifiedOptions;
  }, [lang, amountIncomeDeductionOptions]);

  const modifiedEmploymentTypeOptions = useMemo(() => {
    const modifiedOptions = employmentTypeOptions?.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
      value: otp.id,
    }));
    return modifiedOptions?.sort((a, b) =>
      a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" })
    );
  }, [lang, employmentTypeOptions]);

  const modifiedEducationDegreeOptions = useMemo(() => {
    const modifiedOptions = educationDegreeOptions?.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
      value: otp.id,
    }));
    return modifiedOptions;
  }, [lang, educationDegreeOptions]);

  const modifiedInformationTypeOptions = useMemo(() => {
    const modifiedOptions = informationTypeOptions?.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
      value: otp.id,
    }));
    return modifiedOptions;
  }, [lang, informationTypeOptions]);

  const modifiedBankOptions = useMemo(() => {
    const modifiedOptions = bankOptions?.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
      value: otp.id,
    }));
    return modifiedOptions;
  }, [lang, bankOptions]);

  const modifiedMovementGroupOptions = useMemo(() => {
    const modifiedOptions = movementGroupOptions?.map((otp) => ({
      ...otp,
      label: lang === "en" ? otp.nameEn : otp.name,
    }));
    return modifiedOptions;
  }, [movementGroupOptions, lang]);

  const getMobile = useCallback(
    async (cb = () => { }) => {
      if (shouldSkipFetch("mobile", mobileCodes?.length > 0)) {
        cb(mobileCodes);
        return;
      }
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getMobileCodes();
        cb(response.data);
        dispatch(slices.setMobileCodes(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get mobile codes failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, mobileCodes, shouldSkipFetch]);

  const getMstCompanyTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("companyType", companyTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getMstCompanyType();
      dispatch(slices.setCompanyTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, companyTypeOptions, shouldSkipFetch]);

  const getBankOptions = useCallback(async () => {
    if (shouldSkipFetch("bank", bankOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getBankOptions();
      dispatch(slices.setBankOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, bankOptions, shouldSkipFetch]);

  const getEmploymentTypeOptions = useCallback(async (params) => {
    if (shouldSkipFetch("employmentType", employmentTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getEmploymentTypeOptions({ ...params });
      dispatch(slices.setEmploymentTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get Employee type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, employmentTypeOptions, shouldSkipFetch]);

  const getInformationTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("informationType", informationTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getInformationTypeOptions();
      dispatch(slices.setInformationTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get education degree type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, informationTypeOptions, shouldSkipFetch]);

  const getEducationDegreeOptions = useCallback(async () => {
    if (shouldSkipFetch("educationDegree", educationDegreeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getEducationDegree();
      dispatch(slices.setEducationDegreeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get education degree type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, educationDegreeOptions, shouldSkipFetch]);

  const getBusinessTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("businessType", businessTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getBusinessType();
      dispatch(slices.setBusinessTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, businessTypeOptions, shouldSkipFetch]);

  const getProvinceOptions = useCallback(async () => {
    if (shouldSkipFetch("province", provinceOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getProvinceOptions();
      dispatch(slices.setProvinceOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get province failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, provinceOptions, shouldSkipFetch]);

  const getDistrictOptions = useCallback(
    async (params = {}) => {
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getDistrictOptions(params);
        dispatch(slices.setDistrictOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get district failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, districtOptions, shouldSkipFetch]);

  const getSubDistrictOptions = useCallback(
    async (params = {}) => {
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getSubDistrictOptions(params);
        dispatch(slices.setSubDistrictOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get sub district failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, subDistrictOptions, shouldSkipFetch]);

  const getRegisteredProvinceOptions = useCallback(async () => {
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getProvinceOptions();
      dispatch(slices.setRegisteredProvinceOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get province failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, registered_provinceOptions, shouldSkipFetch]);

  const getRegisteredDistrictOptions = useCallback(
    async (params = {}) => {
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getDistrictOptions(params);
        dispatch(slices.setRegisteredDistrictOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get district failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, registered_districtOptions, shouldSkipFetch]);

  const getRegisteredSubDistrictOptions = useCallback(
    async (params = {}) => {
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getSubDistrictOptions(params);
        dispatch(slices.setRegisteredSubDistrictOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get sub district failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, registered_subDistrictOptions, shouldSkipFetch]);

  const clearDistrictOptions = useCallback(() => {
    dispatch(slices.setDistrictOptions([]));
  }, [dispatch]);

  const clearSubDistrictOptions = useCallback(() => {
    dispatch(slices.setSubDistrictOptions([]));
  }, [dispatch]);

  const clearRegisteredDistrictOptions = useCallback(() => {
    dispatch(slices.setRegisteredDistrictOptions([]));
  }, [dispatch]);

  const clearRegisteredSubDistrictOptions = useCallback(() => {
    dispatch(slices.setRegisteredSubDistrictOptions([]));
  }, [dispatch]);

  const getShiftConditionChangeOptions = useCallback(async () => {
    if (shouldSkipFetch("shiftConditionChange", shiftConditionChangeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getShiftConditionChangeOptions();
      dispatch(slices.setShiftConditionChangeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, shiftConditionChangeOptions, shouldSkipFetch]);

  const getOtReqConditionOptions = useCallback(async () => {
    if (shouldSkipFetch("otReqCondition", otReqConditionOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getOtReqConditionOptions();
      dispatch(slices.setOtReqConditionOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company options failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, otReqConditionOptions, shouldSkipFetch]);

  const getBreakPeriodTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("breakPeriodType", breakPeriodTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getBreakPeriodTypeOptions();
      dispatch(slices.setBreakPeriodTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company options failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, breakPeriodTypeOptions, shouldSkipFetch]);

  const getHolidayTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("holidayType", holidayTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getHolidayTypeOptions();
      dispatch(slices.setHolidayTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company options failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, holidayTypeOptions, shouldSkipFetch]);

  const getDeviceTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("deviceType", deviceTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getDeviceTypeOptions();
      dispatch(slices.setDeviceTypeOptions(response.data?.filter(o => o?.code?.match?.("(DEVICE_TYPE_01|DEVICE_TYPE_02)"))));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get company options failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, deviceTypeOptions, shouldSkipFetch]);

  const getIncomeDeductionByTypeOptions = useCallback(
    async (params = {}) => {
      if (shouldSkipFetch("incomeDeductionByType", incomeDeductionByTypeOptions?.length > 0)) return;
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getIncomeDeductionByTypeOptions(params);
        dispatch(slices.setIncomeDeductionByTypeOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get Income/Deduction type failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, incomeDeductionByTypeOptions, shouldSkipFetch]);

  const getCommonTypeByGroupType = useCallback(
    async (params) => {
      if (shouldSkipFetch("commonTypeByGroupType", false)) return;
      try {
        const response = await services.getCommonTypeByGroupType({
          ...params,
          companyId: currentCompany,
          groupType: "DEDUCTION_TYPE"
        })
        dispatch(slices.setCommonTypeByGroupType(response?.data))
        dispatch(slices.setNoAmountOptions(response?.data?.noAmount))
        dispatch(slices.setNoWorkHoursOptions(response?.data?.noWorkHours))
        dispatch(slices.setNoWorkDaysOptions(response?.data?.noWorkdays))
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(slices.setIsLoading(false))
      }
    }, [currentCompany, dispatch, shouldSkipFetch])

  const getDeductionByTypeOptions = useCallback(
    async (params = {}) => {
      if (shouldSkipFetch("deductionByType", deductionByTypeOptions?.length > 0)) return;
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getIncomeDeductionByTypeOptions({
          ...params,
          type: "Deduction"
        });
        dispatch(slices.setDeductionByTypeOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get Income/Deduction type failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, deductionByTypeOptions, shouldSkipFetch]);

  const getIncomeByCompanyOptions = useCallback(
    async (params = {}) => {
      if (shouldSkipFetch("incomeByCompany", incomeByCompanyOptions?.length > 0)) return;
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getIncomeByCompanyOptions(params);
        dispatch(slices.setIncomeByCompanyOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get Income/Deduction type failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, incomeByCompanyOptions, shouldSkipFetch]);

  const getDeductByCompanyOptions = useCallback(
    async (params = {}) => {
      if (shouldSkipFetch("deductByCompany", deductByCompanyOptions?.length > 0)) return;
      try {
        dispatch(slices.setIsLoading(true));
        const response = await services.getDeductByCompanyOptions(params);
        dispatch(slices.setDeductByCompanyOptions(response.data));
      } catch (error) {
        alert({
          type: "error",
          resultObject: error,
          message: "Get Income/Deduction type failed!!!",
        });
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    },
    [dispatch, deductByCompanyOptions, shouldSkipFetch]);

  const getRoundingTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("roundingType", roundingTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getRoundingTypeOptions();
      dispatch(slices.setRoundingTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get rounding type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, roundingTypeOptions, shouldSkipFetch]);

  const getEmploymentAllOptions = useCallback(async () => {
    if (shouldSkipFetch("employmentAll", employmentSetupOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getEmploymentAllOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: generateRandomString(),
      });
      dispatch(slices.setEmploymentSetupOptions(response?.data?.employmentTypeSetupList));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get rounding type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, currentCompany, currentEmployee, employmentSetupOptions, shouldSkipFetch]);

  const getFrequencyOfPaymentOptions = useCallback(async () => {
    if (shouldSkipFetch("frequencyOfPayment", frequencyOfPaymentOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getFrequencyOfPaymentOptions();
      dispatch(slices.setFrequencyOfPaymentOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get rounding type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, frequencyOfPaymentOptions, shouldSkipFetch]);

  const getTaxCalculationOptions = useCallback(async () => {
    if (shouldSkipFetch("taxCalculation", taxCalculationOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getTaxCalculationOptions();
      dispatch(slices.setTaxCalculationOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get tax calculation type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, taxCalculationOptions, shouldSkipFetch]);

  const getReasonForSSOOptions = useCallback(async () => {
    if (shouldSkipFetch("reasonForSSO", reasonForSSOOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getReasonForSSOOptions();
      dispatch(slices.setReasonForSSOOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get reason for SSO option failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, reasonForSSOOptions, shouldSkipFetch]);

  const getReasonForSeparationOptions = useCallback(async (params) => {
    if (shouldSkipFetch("reasonForSeparation", reasonForSeparationOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getReasonForSeparationOptions({ ...params });
      dispatch(slices.setReasonForSeparationOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get Reason for separation failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, reasonForSeparationOptions, shouldSkipFetch]);

  const getEmployeeTypeOptions = useCallback(async (params) => {
    if (shouldSkipFetch("employeeType", employeeTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getEmployeeTypeOptions({
        ...params
      });
      dispatch(slices.setEmployeeTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get empolyee type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, employeeTypeOptions, shouldSkipFetch]);

  const getEmployeeStstuaTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("employeeStatusType", employeeStatusTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getEmployeeStatusTypeOptions();
      dispatch(slices.setEmployeeStatusTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get empolyee type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, employeeStatusTypeOptions, shouldSkipFetch]);

  const getTitleOptions = useCallback(async () => {
    if (shouldSkipFetch("titleName", titleNameOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getTitleNameOptions();
      dispatch(slices.setTitleNameOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get empolyee type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, titleNameOptions, shouldSkipFetch]);

  const getTitleEnOptions = useCallback(async () => {
    if (shouldSkipFetch("titleNameEn", titleNameEnOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getTitleNameEnOptions();
      dispatch(slices.setTitleNameEnOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get empolyee type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, titleNameEnOptions, shouldSkipFetch]);

  const getWelfareTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("welfareType", welfareTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getWelfareTypeOptions();
      dispatch(slices.setWelfareTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get welfare type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, welfareTypeOptions, shouldSkipFetch]);

  const getRolePermission = useCallback(async () => {
    if (shouldSkipFetch("rolePermission", rolePermissionOptions?.length > 0)) return;
    if (!currentCompany) return null;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getRoleOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(slices.setRolePermission(response?.data?.content));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get role type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, dispatch, rolePermissionOptions, shouldSkipFetch]);

  const getPeriodTimeUnitOptions = useCallback(async () => {
    if (shouldSkipFetch("periodTimeUnit", periodTimeUnitOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getPeriodTimeUnitOptions();
      dispatch(slices.setPeriodTimeUnitOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get period time unit type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, periodTimeUnitOptions, shouldSkipFetch]);

  const getSocialSecurityBranchOption = useCallback(async () => {
    if (shouldSkipFetch("socialSecurityBranch", socialSecurityBranchOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getSocialSecurityBranch({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(slices.setSocialSecurityBranchOption(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get social security branch failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, currentCompany, socialSecurityBranchOptions, shouldSkipFetch]);

  const getGenderOptions = useCallback(async () => {
    if (shouldSkipFetch("gender_options", genderOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getGenderOptions();
      dispatch(slices.setGenderOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get period time unit type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, genderOptions, shouldSkipFetch]);

  const getGender = useCallback(async () => {
    if (shouldSkipFetch("gender", gender?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getGender();
      dispatch(slices.setGender(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get period time unit type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, gender, shouldSkipFetch]);

  const getWelfareLimitOptions = useCallback(async () => {
    if (shouldSkipFetch("welfareLimit", welfareLimitOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getWelfareLimitOptions();
      dispatch(slices.setWelfareLimitOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get welfare limit type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, welfareLimitOptions, shouldSkipFetch]);

  const getAttendanceConditionOptions = useCallback(async () => {
    if (shouldSkipFetch("attendanceCondition", attendanceConditionOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getAttendanceConditionOptions();
      dispatch(slices.setAttendanceConditionOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get attendance condition type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, attendanceConditionOptions, shouldSkipFetch]);

  const getCheckInChanelOptions = useCallback(async () => {
    if (shouldSkipFetch("checkInChanel", checkInChanelOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getCheckInChanelOptions();
      dispatch(slices.setCheckInChanelOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get check-in channel failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, checkInChanelOptions, shouldSkipFetch]);

  const getWeekDayWorkingOptions = useCallback(async () => {
    if (shouldSkipFetch("weekDayWorking", weekDayWorkingOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getWeekDayWorkingOptions();
      dispatch(slices.setWeekDayWorkingOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get week day working type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, weekDayWorkingOptions, shouldSkipFetch]);

  const getWeekWorkingOptions = useCallback(async () => {
    if (shouldSkipFetch("weekWorking", weekWorkingOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getWeekWorkingOptions();
      dispatch(slices.setWeekWorkingOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get week day working type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, weekWorkingOptions, shouldSkipFetch]);

  const getSetAcountTypeOption = useCallback(async () => {
    if (shouldSkipFetch("accountType", acountTypeOption?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getsetAcountTypeOption({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(slices.setAcountTypeOption(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get account type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, dispatch, acountTypeOption, shouldSkipFetch]);

  const getWelfarePaidOptions = useCallback(async () => {
    if (shouldSkipFetch("welfarePaid", welfarePaidOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getWelfarePaidOptions();
      dispatch(slices.setWelfarePaidOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get welfare paid failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, welfarePaidOptions, shouldSkipFetch]);

  const getTypeOfSlipOptions = useCallback(async () => {
    if (shouldSkipFetch("typeOfSlip", typeOfSlipOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getTypeOfSlipOptions();
      dispatch(slices.setTypeOfSlipOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get type of slip failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, typeOfSlipOptions, shouldSkipFetch]);

  const getLeaveGroupOptions = useCallback(async () => {
    if (shouldSkipFetch("leaveGroup", leaveGroupOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getLeaveGroupOptions();
      dispatch(slices.setLeaveGroupOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get leave group failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, leaveGroupOptions, shouldSkipFetch]);

  const getLeaveRequestConditionOptions = useCallback(async () => {
    if (shouldSkipFetch("leaveRequestCondition", leaveRequestConditionOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getLeaveRequestConditionOptions();
      dispatch(slices.setLeaveRequestConditionOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get leave group failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, leaveRequestConditionOptions, shouldSkipFetch]);

  const getMinimumLeaveGroupOptions = useCallback(async () => {
    if (shouldSkipFetch("minimumLeaveGroup", minimumLeaveGroupOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getMinimumLeaveGroup();
      dispatch(slices.setMinimumLeaveGroupOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get Minimum LeaveGroup type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, minimumLeaveGroupOptions, shouldSkipFetch]);

  const getDocumentLimitGroupOptions = useCallback(async () => {
    if (shouldSkipFetch("documentLimitGroup", documentLimitGroupOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getDocumentLimitGroup();
      dispatch(slices.setDocumentLimitGroupOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get Document Limit type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, documentLimitGroupOptions, shouldSkipFetch]);

  const getMonthWeekDayWorkingOptions = useCallback(async () => {
    if (shouldSkipFetch("monthWeekDayWorking", monthWeekDayWorkingOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getMonthMstWeekDayWorking();
      dispatch(slices.setMonthWeekDayWorkingOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get month week day working failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, monthWeekDayWorkingOptions, shouldSkipFetch]);

  const getLeaveCalculationOptions = useCallback(async () => {
    if (shouldSkipFetch("leaveCalculation", leaveCalculationOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getLeaveCalculationOptions();
      dispatch(slices.setLeaveCalculationOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get leave calculation failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, leaveCalculationOptions, shouldSkipFetch]);

  const getRaceOptions = useCallback(async () => {
    if (shouldSkipFetch("race", raceOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getRaceOptions();
      dispatch(slices.setRaceOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get Income Deduction failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, raceOptions, shouldSkipFetch]);

  const getCountryOptions = useCallback(async () => {
    if (shouldSkipFetch("country", countryOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getCountryOptions();
      dispatch(slices.setCountryOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get country failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, countryOptions, shouldSkipFetch]);

  const getVisaTypeOptions = useCallback(async () => {
    if (shouldSkipFetch("visaType", visaTypeOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getVisaType();
      dispatch(slices.setVisaTypeOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get visa type failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, visaTypeOptions, shouldSkipFetch]);

  const getAmountIncomeDeductionOptions = useCallback(async () => {
    if (shouldSkipFetch("amountIncomeDeduction", amountIncomeDeductionOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getAmountIncomeDeductionOptions();
      dispatch(slices.setAmountIncomeDeductionOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get Income Deduction failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, amountIncomeDeductionOptions, shouldSkipFetch]);

  const getNationalityOptions = useCallback(async () => {
    if (shouldSkipFetch("nationality", nationalityOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getNationalityOptions();
      dispatch(slices.setNationalityOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get nationality failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, nationalityOptions, shouldSkipFetch]);

  const getReligionOptions = useCallback(async () => {
    if (shouldSkipFetch("religion", religionOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getReligionOptions();
      dispatch(slices.setReligionOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get religion failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, religionOptions, shouldSkipFetch]);

  const getMaritalStatusOptions = useCallback(async () => {
    if (shouldSkipFetch("maritalStatus", maritalStatusOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getMaritalStatusOptions();
      dispatch(slices.setMaritalStatusOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get marital status failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, maritalStatusOptions, shouldSkipFetch]);

  const getMilitaryStatusOptions = useCallback(async () => {
    if (shouldSkipFetch("militaryStatus", militaryStatusOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getMilitaryStatusOptions();
      dispatch(slices.setMilitaryStatusOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get military status failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, militaryStatusOptions, shouldSkipFetch]);

  const getBloodGroupOptions = useCallback(async () => {
    if (shouldSkipFetch("bloodGroup", bloodGroupOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getBloodGroupOptions();
      dispatch(slices.setBloodGroupOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get blood group status failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, bloodGroupOptions, shouldSkipFetch]);

  const getRelationshipOptions = useCallback(async () => {
    if (shouldSkipFetch("relationship", relationshipOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getRelationshipOptions();
      dispatch(slices.setRelationshipOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get relationship status failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, relationshipOptions, shouldSkipFetch]);

  const getOccupationOptions = useCallback(async (params) => {
    if (shouldSkipFetch("occupation", occupationOptions?.length > 0)) return;
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getOccupationOptions({
        ...params
      });
      dispatch(slices.setOccupationOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get occupation status failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, occupationOptions, shouldSkipFetch]);

  const isFetchingMovementGroupRef = useRef(false);
  const getMovementGroupOptions = useCallback(async (params) => {
    if (shouldSkipFetch("movementGroup", movementGroupOptions?.length > 0)) return;
    if (isFetchingMovementGroupRef.current) return;
    try {
      isFetchingMovementGroupRef.current = true;
      dispatch(slices.setIsLoading(true));
      const response = await services.getMovementGroup({
        ...params
      });
      dispatch(slices.setMovementGroupOptions(response.data));
    } catch (error) {
      alert({
        type: "error",
        resultObject: error,
        message: "Get occupation status failed!!!",
      });
    } finally {
      dispatch(slices.setIsLoading(false));
      isFetchingMovementGroupRef.current = false;
    }
  }, [dispatch, movementGroupOptions, shouldSkipFetch]);

  const getDepartment = useCallback(async () => {
    if (shouldSkipFetch("department", false)) return;
    try {
      const response = await services.getDepartmentOptions({
        companyId: currentCompany,
      })
      dispatch(slices.setDepartment(response?.data))
    } catch (error) {
      // no-op, mirrors original behavior
    }
  }, [currentCompany, shouldSkipFetch])

  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language, setLang]);

  return {
    isLoading,
    mobileCodes: modifiedMobileCodes,
    companyTypeOptions: modifiedMstCompanyTypeOptions,
    businessTypeOptions: modifiedBusinessTypeOptions,
    provinceOptions: modifiedProvinceOptions,
    districtOptions: modifiedDistrictOptions,
    subDistrictOptions: modifiedSubDistrictOptions,
    sourceProvinceOptions: provinceOptions,
    sourceDistrictOptions: districtOptions,
    sourceSubDistrictOptions: subDistrictOptions,
    registered_sourceProvinceOptions: registered_provinceOptions,
    registered_sourceDistrictOptions: registered_districtOptions,
    registered_sourceSubDistrictOptions: registered_subDistrictOptions,
    shiftConditionChangeOptions: modifiedShiftConditionChangeOptions,
    otReqConditionOptions: modifiedOtReqConditionOptions,
    breakPeriodTypeOptions: modifiedBreakPeriodTypeOptions,
    holidayTypeOptions: modifiedHolidayTypeOptions,
    deviceTypeOptions: modifiedDeviceTypeOptions,
    incomeDeductionByTypeOptions: modifiedIncomeDeductionByTypeOptions,
    deductionByTypeOptions: modifiedDeductionByTypeOptions,
    deductByCompanyOptions: modifiedDeductByCompanyOptions,
    incomeByCompanyOptions: modifiedIncomeByCompanyOptions,
    noAmountOptions: modifiedNoAmountOptions,
    noWorkHoursOptions: modifiedNoWorkHoursOptions,
    noWorkDaysOptions: modifiedNoWorkDaysOptions,
    roundingTypeOptions: modifiedRoundingTypeOptions,
    employmentSetupOptions: modifiedEmploymentSetupOptions,
    frequencyOfPaymentOptions: modifiedFrequencyOfPaymentOptions,
    taxCalculationOptions: modifiedTaxCalculationOptions,
    reasonForSSOOptions: modifiedReasonForSSOOptions,
    employeeTypeOptions: modifiedEmployeeTypeOptions,
    titleNameOptions: modifiedTitleNameOptions,
    titleNameEnOptions: modifiedTitleNameEnOptions,
    welfareTypeOptions: modifiedWelfareTypeOptions,
    rolePermissionOptions: modifiedRolePermissionOptions,
    periodTimeUnitOptions: modifiedPeriodTimeUnitOptions,
    genderOptions: modifiedGenderOptions,
    gender: modifiedGender,
    welfareLimitOptions: modifiedWelfareLimitOptions,
    attendanceConditionOptions: modifiedAttendanceConditionOptions,
    checkInChanelOptions: modifiedCheckInChanelOptions,
    weekDayWorkingOptions: modifiedWeekDayWorkingOptions,
    weekWorkingOptions: modifiedWeekWorkingOptions,
    socialSecurityBranchOptions: modifiedSocialSecurityBranchOptions,
    acountTypeOption: modifiedAcountTypeOption,
    welfarePaidOptions: modifiedWelfarePaidOptions,
    typeOfSlipOptions: modifiedTypeOfSlipOptions,
    leaveGroupOptions: modifiedLeaveGroupOptions,
    leaveRequestConditionOptions: modifiedLeaveRequestConditionOptions,
    minimumLeaveGroupOptions: modifiedMinimumLeaveGroupOptions,
    documentLimitGroupOptions: modifiedDocumentLimitGroupOptions,
    monthWeekDayWorkingOptions: modifiedMonthWeekDayWorkingOptions,
    leaveCalculationOptions: modifiedLeaveCalculationOptions,
    raceOptions: modifiedRaceOptions,
    countryOptions: modifiedCountryOptions,
    visaTypeOptions: modifiedVisaTypeOptions,
    nationalityOptions: modifiedNationalityOptions,
    religionOptions: modifiedReligionOptions,
    maritalStatusOptions: modifiedMaritalStatusOptions,
    militaryStatusOptions: modifiedMilitaryStatusOptions,
    bloodGroupOptions: modifiedBloodGroupOptions,
    relationshipOptions: modifiedRelationshipOptions,
    occupationOptions: modifiedOccupationOptions,
    amountIncomeDeductionOptions: modifiedAmountIncomeDeductionOptions,
    employeeStatusTypeOptions: modifiedEmployeeStatusTypeOptions,
    employmentTypeOptions: modifiedEmploymentTypeOptions,
    informationTypeOptions: modifiedInformationTypeOptions,
    educationDegreeOptions: modifiedEducationDegreeOptions,
    bankOptions: modifiedBankOptions,
    movementGroupOptions: modifiedMovementGroupOptions,
    reasonForSeparationOptions: modifiedReasonForSeparationOptions,
    getTitleOptions,
    getTitleEnOptions,
    getMobile,
    getMstCompanyTypeOptions,
    getBusinessTypeOptions,
    getProvinceOptions,
    getDistrictOptions,
    getSubDistrictOptions,
    getRegisteredProvinceOptions,
    getRegisteredDistrictOptions,
    getRegisteredSubDistrictOptions,
    clearDistrictOptions,
    clearSubDistrictOptions,
    clearRegisteredDistrictOptions,
    clearRegisteredSubDistrictOptions,
    getShiftConditionChangeOptions,
    getOtReqConditionOptions,
    getBreakPeriodTypeOptions,
    getHolidayTypeOptions,
    getDeviceTypeOptions,
    getIncomeDeductionByTypeOptions,
    getCommonTypeByGroupType,
    getDeductionByTypeOptions,
    getIncomeByCompanyOptions,
    getDeductByCompanyOptions,
    getRoundingTypeOptions,
    getEmploymentAllOptions,
    getFrequencyOfPaymentOptions,
    getTaxCalculationOptions,
    getReasonForSSOOptions,
    getEmployeeTypeOptions,
    getWelfareTypeOptions,
    getRolePermission,
    getPeriodTimeUnitOptions,
    getGenderOptions,
    getWelfareLimitOptions,
    getAttendanceConditionOptions,
    getCheckInChanelOptions,
    getWeekDayWorkingOptions,
    getWeekWorkingOptions,
    getSocialSecurityBranchOption,
    getSetAcountTypeOption,
    getWelfarePaidOptions,
    getTypeOfSlipOptions,
    getLeaveGroupOptions,
    getLeaveRequestConditionOptions,
    getMinimumLeaveGroupOptions,
    getDocumentLimitGroupOptions,
    getMonthWeekDayWorkingOptions,
    getLeaveCalculationOptions,
    getRaceOptions,
    getCountryOptions,
    getVisaTypeOptions,
    getNationalityOptions,
    getReligionOptions,
    getMaritalStatusOptions,
    getMilitaryStatusOptions,
    getBloodGroupOptions,
    getRelationshipOptions,
    getOccupationOptions,
    getAmountIncomeDeductionOptions,
    getEmployeeStstuaTypeOptions,
    getEmploymentTypeOptions,
    getInformationTypeOptions,
    getEducationDegreeOptions,
    getBankOptions,
    getMovementGroupOptions,
    getDepartment,
    getGender,
    getReasonForSeparationOptions
  };
}

export default useOptions;